import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  CardGiftcard as GiftIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  EmojiEvents as TrophyIcon,
  ConfirmationNumber as TicketIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
  Group as GroupIcon,
  EmailOutlined as EmailOutlinedIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import {
  sendWinnerEmail,
  sendParticipantEmail,
} from "../../features/giveaways/giveawaysSlice";
import WinnerNotificationDialog from "./WinnerNotificationDialog";

const GiveawayDetailDialog = ({ open, onClose, giveaway }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.giveaways);

  const [winnerNotificationOpen, setWinnerNotificationOpen] = useState(false);
  const [sendingWinnerEmail, setSendingWinnerEmail] = useState(false);
  const [sendingParticipantEmail, setSendingParticipantEmail] = useState(false);

  if (!giveaway) {
    return null;
  }

  const formatDate = (date) => {
    if (!date) return "N/A";
    return format(new Date(date), "dd/MM/yyyy HH:mm");
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "completed":
        return "info";
      case "cancelled":
        return "error";
      case "upcoming":
        return "warning";
      default:
        return "default";
    }
  };

  const winner = giveaway.winner || {};

  // Default email templates
  const defaultWinnerEmailTemplate = {
    subject: "Congratulations! You've Won the {{giveaway_name}}",
    body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f8f9fa;">
  <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
    <h1 style="color: white; margin: 0; font-size: 28px;">🎉 Congratulations! 🎉</h1>
  </div>
  
  <div style="background: white; padding: 30px; border-radius: 0 0 10px 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
    <h2 style="color: #333; margin-bottom: 20px;">Dear {{winner_name}},</h2>
    
    <p style="font-size: 16px; line-height: 1.6; color: #555;">We're thrilled to announce that you've won our <strong>{{giveaway_name}}</strong> giveaway!</p>
    
    <div style="background: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #4caf50;">
      <h3 style="color: #2e7d32; margin: 0 0 10px 0;">Prize Details:</h3>
      <p style="margin: 0; font-size: 16px; color: #333;"><strong>{{prize_description}}</strong></p>
    </div>
    
    <h3 style="color: #333; margin-top: 25px;">Next Steps:</h3>
    <div style="text-align: center; margin: 30px 0;">
      <a href="{{claim_url}}" style="display: inline-block; background: linear-gradient(135deg, #4caf50 0%, #45a049 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3); transition: all 0.3s ease;">🎁 Claim Your Prize Now</a>
    </div>
    <p style="color: #555; line-height: 1.6; text-align: center; font-size: 14px;">Click the button above to claim your prize, or copy and paste this link into your browser:<br><span style="color: #007bff; word-break: break-all;">{{claim_url}}</span></p>
    
    <h4 style="color: #333; margin-top: 25px;">After claiming:</h4>
    <ol style="color: #555; line-height: 1.6;">
      <li>Provide your shipping address and contact information</li>
      <li>We'll process and ship your prize within 5-10 business days</li>
    </ol>
    
    <div style="background: #fff3cd; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #ffc107;">
      <p style="margin: 0; color: #856404;"><strong>Important:</strong> You have 7 days from the date of this email to claim your prize. Unclaimed prizes will be forfeited.</p>
    </div>
    
    <p style="color: #555; line-height: 1.6;">Thank you for being a valued member of Nature's Perks! We hope you enjoy your prize.</p>
    
    <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
      <p style="color: #888; margin: 0;">Best regards,<br><strong>The Nature's Perks Team</strong></p>
    </div>
  </div>
</div>`,
  };

  const defaultParticipantEmailTemplate = {
    subject: "Thank you for participating in {{giveaway_name}}",
    body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f8f9fa;">
  <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
    <h1 style="color: white; margin: 0; font-size: 28px;">🎉 Winner Announcement! 🎉</h1>
  </div>
  
  <div style="background: white; padding: 30px; border-radius: 0 0 10px 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
    <h2 style="color: #333; margin-bottom: 20px;">Dear Nature's Perks Member,</h2>
    
    <p style="font-size: 16px; line-height: 1.6; color: #555;">We're excited to announce the winner of our <strong>{{giveaway_name}}</strong> giveaway!</p>
    
    <div style="background: #e3f2fd; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #2196f3; text-align: center;">
      <h3 style="color: #1976d2; margin: 0 0 10px 0;">🏆 Congratulations to:</h3>
      <p style="margin: 0; font-size: 20px; font-weight: bold; color: #333;">{{winner_name}}</p>
    </div>
    
    <div style="background: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #4caf50;">
      <h3 style="color: #2e7d32; margin: 0 0 10px 0;">Prize Won:</h3>
      <p style="margin: 0; font-size: 16px; color: #333;"><strong>{{prize_description}}</strong></p>
    </div>
    
    <p style="color: #555; line-height: 1.6;">Don't worry if you didn't win this time - we have more exciting giveaways coming up! Keep an eye on your email for future opportunities.</p>
    
    <div style="text-align: center; margin: 20px 0;">
      <a href="#" style="background: #4caf50; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">View Upcoming Giveaways</a>
    </div>
    
    <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
      <p style="color: #888; margin: 0;">Thank you for being part of Nature's Perks!<br><strong>The Nature's Perks Team</strong></p>
    </div>
  </div>
</div>`,
  };

  // Email handling functions
  const handleSendWinnerEmail = async () => {
    setSendingWinnerEmail(true);
    try {
      await dispatch(
        sendWinnerEmail({
          giveawayId: giveaway._id || giveaway.id,
          subject: defaultWinnerEmailTemplate.subject,
          body: defaultWinnerEmailTemplate.body,
        })
      );
    } catch (error) {
      console.error("Error sending winner email:", error);
    } finally {
      setSendingWinnerEmail(false);
    }
  };

  const handleSendParticipantEmail = async () => {
    setSendingParticipantEmail(true);
    try {
      await dispatch(
        sendParticipantEmail({
          giveawayId: giveaway._id || giveaway.id,
          subject: defaultParticipantEmailTemplate.subject,
          body: defaultParticipantEmailTemplate.body,
        })
      );
    } catch (error) {
      console.error("Error sending participant email:", error);
    } finally {
      setSendingParticipantEmail(false);
    }
  };

  const handleOpenWinnerNotification = () => {
    setWinnerNotificationOpen(true);
  };

  const handleCloseWinnerNotification = () => {
    setWinnerNotificationOpen(false);
  };

  const formatEmailStatus = (emailSent, emailSentDate) => {
    if (emailSent && emailSentDate) {
      return `Sent on ${formatDate(emailSentDate)}`;
    }
    return "Not sent";
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <GiftIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="h6">Giveaway Details</Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {/* Giveaway Information */}
            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <InfoIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="h6">Giveaway Information</Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        mr: 2,
                        bgcolor: "primary.main",
                        color: "#ffffff",
                      }}
                    >
                      <GiftIcon fontSize="large" color="white" />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {giveaway.title}
                      </Typography>
                      <Chip
                        label={
                          giveaway.status?.charAt(0).toUpperCase() +
                            giveaway.status?.slice(1) || "Active"
                        }
                        color={getStatusColor(giveaway.status)}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Description:</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {giveaway.description || "No description available"}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Prize:</strong> {giveaway.prize || "N/A"}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <CalendarIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <Typography variant="body2">
                      <strong>Created:</strong>{" "}
                      {formatDate(giveaway.created_date || giveaway.createdAt)}
                    </Typography>
                  </Box>

                  {giveaway.draw_date && (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <CalendarIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography variant="body2">
                        <strong>Draw Date:</strong>{" "}
                        {formatDate(giveaway.draw_date)}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Statistics & Winner Information */}
            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <TrophyIcon sx={{ mr: 1, color: "warning.main" }} />
                    <Typography variant="h6">Statistics & Winner</Typography>
                  </Box>

                  {/* Statistics */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Participation Stats
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" color="primary.main">
                          {giveaway.total_participants || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Participants
                        </Typography>
                      </Box>
                      {/* <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" color="success.main">
                          {giveaway.total_entries || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Entries
                        </Typography>
                      </Box> */}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Winner Information */}
                  {winner && (winner.first_name || winner.email) ? (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Winner Information
                      </Typography>

                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: 50,
                            height: 50,
                            mr: 2,
                            bgcolor: "success.main",
                            color: "#ffffff",
                          }}
                        >
                          <TrophyIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1">
                            {winner.first_name} {winner.last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {winner.email}
                          </Typography>
                        </Box>
                      </Box>

                      {winner.entries_used && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Entries Used:</strong> {winner.entries_used}
                        </Typography>
                      )}

                      {winner.remaining_entries && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Remaining Entries:</strong>{" "}
                          {winner.remaining_entries}
                        </Typography>
                      )}

                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 2 }}
                      >
                        <CheckCircleIcon
                          fontSize="small"
                          sx={{ mr: 1, color: "success.main" }}
                        />
                        <Typography variant="body2" color="success.main">
                          Winner Selected
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Winner Email Status */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Winner Email Status
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <EmailIcon
                            fontSize="small"
                            sx={{
                              mr: 1,
                              color: giveaway.winner_email_sent
                                ? "success.main"
                                : "text.secondary",
                            }}
                          />
                          <Typography
                            variant="body2"
                            color={
                              giveaway.winner_email_sent
                                ? "success.main"
                                : "text.secondary"
                            }
                          >
                            {formatEmailStatus(
                              giveaway.winner_email_sent,
                              giveaway.winner_email_sent_date
                            )}
                          </Typography>
                        </Box>

                        {!giveaway.winner_email_sent && (
                          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                sendingWinnerEmail ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SendIcon />
                                )
                              }
                              onClick={handleSendWinnerEmail}
                              disabled={sendingWinnerEmail || loading}
                            >
                              Send Winner Email
                            </Button>

                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EmailOutlinedIcon />}
                              onClick={handleOpenWinnerNotification}
                            >
                              Advanced Email
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                      <TrophyIcon
                        sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        No winner selected yet
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <GroupIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="h6">
                      Participant Email Status
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <EmailIcon
                      fontSize="small"
                      sx={{
                        mr: 1,
                        color: giveaway.participant_email_sent
                          ? "success.main"
                          : "text.secondary",
                      }}
                    />
                    <Typography
                      variant="body2"
                      color={
                        giveaway.participant_email_sent
                          ? "success.main"
                          : "text.secondary"
                      }
                      sx={{ mr: 2 }}
                    >
                      {formatEmailStatus(
                        giveaway.participant_email_sent,
                        giveaway.participant_email_sent_date
                      )}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      ({giveaway.total_participants || 0} participants)
                    </Typography>
                  </Box>

                  {!giveaway.participant_email_sent && (
                    <Button
                      variant="outlined"
                      startIcon={
                        sendingParticipantEmail ? (
                          <CircularProgress size={16} />
                        ) : (
                          <SendIcon />
                        )
                      }
                      onClick={handleSendParticipantEmail}
                      disabled={
                        sendingParticipantEmail ||
                        loading ||
                        !giveaway.total_participants
                      }
                    >
                      Send Participant Emails
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Winner Notification Dialog */}
      {winner && (winner.first_name || winner.email) && (
        <WinnerNotificationDialog
          open={winnerNotificationOpen}
          onClose={handleCloseWinnerNotification}
          winner={winner}
          giveaway={giveaway}
        />
      )}
    </>
  );
};

export default GiveawayDetailDialog;
